import React, { Fragment, useEffect, useState } from 'react';
import TableCellFloat from './floats/TableCellFloat';
import TableCellInteger from './integer/TableCellInteger';
import TableCellText from './text/TableCellText';
import TableCellCurrency from './currency/TableCellCurrency';
import TableCellButton from './button/TableCellButton';
import TableCellSelect from './select/TableCellSelect';
import TableCellSliderCheckbox from './slidercheckbox/TableCellSliderCheckbox';
import { StateModel } from '../../../redux/store';
import { connect } from 'react-redux';

interface TableCellProps {
  name: string;
  value: any;
  tableName?: string;
  onChangeHandler?: (
    targetName: string,
    targetValue: any,
    rowIndex: number,
    hierarchicalAttributename?: string
  ) => void;
  onClickHandler?: any;
  onFocusHandler?: any;
  rowIndex: number;
  tabIndex?: number;
  valueType?: string;
  onBlurHandler?: any;
  selectData?: Array<any>;
  selectItemId?: string | undefined;
  classNameSelect?: string;
  edit?: boolean;
  selectByAllTablecells?: boolean;
  specificModal?: string;
  AutoCompleteFormat?: any;
  autoCompleteGetter?: (rowIndex: number, value: string) => void;
  hierarchicalAttributename?: string;
  auftragserfassung: any;
  required?: boolean;
  startingValue?: string;
  filterList?: boolean;
  filterTyp?: string;
  fixedDecimalScale?: boolean;
  searchQueryString?: string;
}

/**
 * Komponente für jede einzelene Zelle einer {@link Table.jsx}
 * @param name - Html Name des Elementes
 * @param value Html Value des Elementes
 * @param onChangeHandler on change handler
 * @param onClickHandler on click handler
 * @param onFocusHandler on focus handler
 * @param onBlurHandler on blur handler
 * @param rowIndex Zeilenindex
 * @param tabIndex tab index (auch -1 or 0), definiert die Tabreihenfolge
 * @param valueType der Typ des Elements
 * @param edit ob das Feld editierbar ist
 * @param selectByAllTablecells
 * @returns {JSX.Element}
 */
const TableCell = ({
  name,
  value,
  tableName,
  onChangeHandler,
  onClickHandler,
  onFocusHandler,
  rowIndex,
  tabIndex = 0,
  valueType,
  onBlurHandler,
  selectData,
  classNameSelect,
  edit,
  selectByAllTablecells,
  specificModal,
  AutoCompleteFormat,
  auftragserfassung,
  autoCompleteGetter,
  hierarchicalAttributename,
  required,
  startingValue,
  filterList,
  filterTyp,
  fixedDecimalScale,
  searchQueryString,
}: TableCellProps) => {
  const inputRef = React.useRef() as React.MutableRefObject<HTMLInputElement>;

  useEffect(() => {
    onBlurHandler && onBlurHandler();
  }, []);

  const [searchValue, setSearchValue] = useState(value);

  const autocompleteHandler = (suchvorschlag: any) => {
    onChangeHandler && onChangeHandler(name, suchvorschlag, rowIndex);
  };

  useEffect(() => {
    if (autoCompleteGetter && inputRef.current === document.activeElement) {
      autoCompleteGetter(rowIndex, searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (autoCompleteGetter) {
      setSearchValue(
        hierarchicalAttributename
          ? value[hierarchicalAttributename] ??
              (typeof value == 'string' ? value : '')
          : ''
      );
    }
  }, [value]);

  const cellContent = () => {
    switch (valueType) {
      case 'float':
        return (
          <TableCellFloat
            name={name}
            value={
              hierarchicalAttributename
                ? value[hierarchicalAttributename] ?? value
                : value ?? ''
            }
            tableName={tableName}
            onChangeHandler={onChangeHandler}
            onFocusHandler={onFocusHandler}
            onClickHandler={selectByAllTablecells && onClickHandler}
            rowIndex={rowIndex}
            tabIndex={tabIndex}
            onBlurHandler={onBlurHandler}
            disabled={!edit}
            required={required}
            fixedDecimalScale={fixedDecimalScale}
          />
        );
      case 'number':
        return (
          <TableCellInteger
            name={name}
            value={
              hierarchicalAttributename
                ? value[hierarchicalAttributename] ?? value
                : value ?? ''
            }
            tableName={tableName}
            onChangeHandler={onChangeHandler}
            onFocusHandler={onFocusHandler}
            onClickHandler={selectByAllTablecells && onClickHandler}
            rowIndex={rowIndex}
            tabIndex={tabIndex}
            onBlurHandler={onBlurHandler}
            disabled={!edit}
            required={required}
          />
        );
      case 'currency':
        return (
          <TableCellCurrency
            name={name}
            value={
              hierarchicalAttributename
                ? value[hierarchicalAttributename] ?? value
                : value ?? ''
            }
            onChangeHandler={onChangeHandler}
            onFocusHandler={onFocusHandler}
            onClickHandler={selectByAllTablecells && onClickHandler}
            rowIndex={rowIndex}
            tabIndex={tabIndex}
            onBlurHandler={onBlurHandler}
            disabled={!edit}
            required={required}
          />
        );
      case 'button':
        return (
          <TableCellButton
            value={value}
            onClickHandler={onClickHandler}
            rowIndex={rowIndex}
            tabIndex={tabIndex}
          />
        );
      case 'slidercheckbox':
      case 'sliderbutton':
        return (
          <TableCellSliderCheckbox
            value={
              hierarchicalAttributename
                ? value[hierarchicalAttributename] ?? value
                : value ?? ''
            }
            name={name}
            hierarchicalAttributename={hierarchicalAttributename}
            onClickHandler={onClickHandler}
            onChangeHandler={onChangeHandler}
            rowIndex={rowIndex}
            disabled={!edit}
            tabIndex={tabIndex}
            SpecificModal={specificModal}
            useAsButton={valueType === 'sliderbutton'}
          />
        );
      case 'select':
        return (
          // Selects brauchen immer einen onChangeHandler
          onChangeHandler &&
          selectData && (
            <TableCellSelect
              name={name}
              tableName={tableName}
              selectedOptionId={typeof value === 'object' ? value?.id : value}
              rowIndex={rowIndex}
              startingValue={
                startingValue ?? value?.bezkurz ?? value?.kurzBezeichnung
              } /* nur für Verpackung und Lademittel relevant, da awSperre = true sein kann, weshalb
                   die ausgewaehlte Option nicht immer in selectData vorhanden ist */
              selectData={selectData}
              onChangeHandler={onChangeHandler}
              className={classNameSelect}
              onBlurHandler={onBlurHandler}
              filterList={filterList}
              filterType={filterTyp}
            />
          )
        );
      default:
        return (
          <TableCellText
            name={name}
            hierarchicalAttributename={hierarchicalAttributename}
            value={
              hierarchicalAttributename
                ? value[hierarchicalAttributename] ??
                  (typeof value == 'string' ? value : '')
                : value ?? ''
            }
            ref={inputRef}
            tableName={tableName}
            onChangeHandler={onChangeHandler}
            onFocusHandler={onFocusHandler}
            onClickHandler={selectByAllTablecells && onClickHandler}
            rowIndex={rowIndex}
            tabIndex={tabIndex}
            onBlurHandler={onBlurHandler}
            disabled={!edit}
            suchvorschlaege={
              AutoCompleteFormat ? auftragserfassung.suchvorschlaege : undefined
            }
            autocompleteFormat={
              AutoCompleteFormat ? <AutoCompleteFormat /> : undefined
            }
            autocompleteHandler={
              AutoCompleteFormat ? autocompleteHandler : undefined
            }
            required={required}
            searchQueryString={
              searchQueryString?.length == 0 ? undefined : searchQueryString
            }
          />
        );
    }
  };

  return (
    <Fragment>
      <td>{cellContent()}</td>
    </Fragment>
  );
};

const mapStateToProps = (state: StateModel) => ({
  auftragserfassung: state.Auftragserfassung,
});

export default connect(mapStateToProps, {})(TableCell);
